import {
  forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import Map, { Marker, Popup, NavigationControl } from 'react-map-gl/maplibre';
import { setWorkerUrl } from 'maplibre-gl';
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import 'maplibre-gl/dist/maplibre-gl.css';
import Avatar from './Avatar.jsx';
import { sharedInterests } from '../lib/interests.js';
import {
  layoutMarkerOffsets, sameMarkerGroups, sameMarkerOffsets,
  shouldSeparateMarkers, stackMarkerGroups,
} from '../lib/markerCollision.js';

// MapLibre 6 ships its worker as a separate ES module. Vite must bundle it
// explicitly; otherwise production mounts a canvas but never parses a tile.
setWorkerUrl(maplibreWorkerUrl);

/**
 * MapLibre GL against OpenFreeMap's hosted OpenStreetMap tiles: no API key, no
 * per-load billing, no rate limit. Markers are real DOM, so an avatar on the
 * map is the same component as an avatar in the people panel, and the popup
 * anchors itself — no hand-rolled projection maths.
 *
 * Positron is deliberately near-greyscale: the avatars are the subject here, so
 * the basemap stays a background and never competes with them.
 */
const STYLE = 'https://tiles.openfreemap.org/styles/positron';

const MapView = forwardRef(function MapView(
  { actors, myInterests, selfActorId, unread, selected,
    onSelect, onCloseSelected, renderSelected, onBoundsChange, footnote },
  ref
) {
  const mapRef = useRef(null);
  const markerRefs = useRef(new globalThis.Map());
  const actorsRef = useRef(actors);
  const collisionFrame = useRef(null);
  // A focus asked for before the map exists, spent once it does.
  const wantedRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [markerOffsets, setMarkerOffsets] = useState({});
  const [markerGroups, setMarkerGroups] = useState([]);
  actorsRef.current = actors;

  const updateCollisionLayout = useCallback(() => {
    const map = mapRef.current?.getMap?.();
    const container = map?.getContainer?.();
    if (!map || !container) return;

    const projected = [];
    for (const actor of actorsRef.current) {
      if (actor.locationSource === 'fallback') continue;
      let point;
      try { point = map.project([actor.lng, actor.lat]); } catch { continue; }
      if (!Number.isFinite(point?.x) || !Number.isFinite(point?.y)) continue;
      const element = markerRefs.current.get(actor.id)?.getElement?.();
      projected.push({
        id: actor.id,
        x: point.x,
        y: point.y,
        lat: actor.lat,
        lng: actor.lng,
        occluded: element?.classList.contains('maplibregl-marker-covered') ?? false,
        priority: actor.id === selected?.id ? 0 : actor.id === selfActorId ? 1 : 10,
      });
    }

    const cityScale = shouldSeparateMarkers(map.getZoom(), map.getCenter().lat);
    const nextOffsets = cityScale
      ? layoutMarkerOffsets(projected, container.clientWidth, container.clientHeight)
      : Object.fromEntries(projected.map((marker) => [marker.id, [0, 0]]));
    const nextGroups = cityScale ? null : stackMarkerGroups(projected);
    setMarkerOffsets((current) => (
      sameMarkerOffsets(current, nextOffsets) ? current : nextOffsets
    ));
    setMarkerGroups((current) => (
      sameMarkerGroups(current, nextGroups) ? current : nextGroups
    ));
  }, [selected?.id, selfActorId]);

  const scheduleCollisionLayout = useCallback(() => {
    if (collisionFrame.current != null) return;
    collisionFrame.current = requestAnimationFrame(() => {
      collisionFrame.current = null;
      updateCollisionLayout();
    });
  }, [updateCollisionLayout]);

  const publishBounds = useCallback(() => {
    const map = mapRef.current?.getMap?.();
    const bounds = map?.getBounds?.();
    if (!bounds) return;
    onBoundsChange?.({
      west: bounds.getWest(), south: bounds.getSouth(),
      east: bounds.getEast(), north: bounds.getNorth(),
    });
  }, [onBoundsChange]);

  useEffect(() => {
    if (ready) scheduleCollisionLayout();
  }, [actors, ready, scheduleCollisionLayout]);

  useEffect(() => () => {
    if (collisionFrame.current != null) cancelAnimationFrame(collisionFrame.current);
  }, []);

  useEffect(() => {
    if (ready) return undefined;
    const timer = setTimeout(() => {
      setMapError('the map renderer did not start — WebGL2 may be unavailable');
    }, 15_000);
    return () => clearTimeout(timer);
  }, [ready]);

  const flyTo = useCallback((lat, lng, zoom) => {
    const map = mapRef.current?.getMap?.();
    if (!map) return false;
    map.flyTo({
      center: [lng, lat],
      zoom,
      pitch: 40,
      speed: 0.9,
      curve: 1.4,
      essential: true,
    });
    return true;
  }, []);

  useImperativeHandle(ref, () => ({
    focus(lat, lng, zoom = 5) {
      // Arriving from a profile mounts this component and asks for the fly in
      // the same commit, before there is a GL map to fly. Silently doing
      // nothing is how that read: the card opened on the right person and the
      // globe stayed where it was. Hold the target and let `ready` spend it.
      if (!flyTo(lat, lng, zoom)) wantedRef.current = { lat, lng, zoom };
    },
  }), [flyTo]);

  useEffect(() => {
    const wanted = wantedRef.current;
    if (!ready || !wanted) return;
    wantedRef.current = null;
    flyTo(wanted.lat, wanted.lng, wanted.zoom);
  }, [ready, flyTo]);

  // Always a globe. setProjection throws if the style has not parsed yet, so
  // wait for it rather than assuming load order.
  useEffect(() => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;

    const apply = () => {
      try {
        map.setProjection({ type: 'globe' });
      } catch (err) {
        setMapError(`projection: ${err.message}`);
      }
    };

    if (map.isStyleLoaded()) apply();
    else map.once('style.load', apply);
  }, [ready]);

  const focusStack = useCallback((members) => {
    const map = mapRef.current?.getMap?.();
    const anchor = members[0];
    if (!map || !anchor) return;

    // Longitudes are unwrapped around the anchor so a stack crossing the date
    // line zooms into that area instead of fitting almost the entire planet.
    const longitudes = members.map((actor) => (
      anchor.lng + ((((actor.lng - anchor.lng) + 540) % 360) - 180)
    ));
    const latitudes = members.map((actor) => actor.lat);
    const west = Math.min(...longitudes);
    const east = Math.max(...longitudes);
    const south = Math.min(...latitudes);
    const north = Math.max(...latitudes);

    if (east - west < 0.0001 && north - south < 0.0001) {
      map.flyTo({ center: [anchor.lng, anchor.lat], zoom: 11.75, essential: true });
      return;
    }
    map.fitBounds([[west, south], [east, north]], {
      padding: 110,
      maxZoom: 11.75,
      duration: 900,
    });
  }, []);

  const markers = useMemo(() => {
    const located = actors.filter((actor) => actor.locationSource !== 'fallback');
    const actorById = new globalThis.Map(located.map((actor) => [actor.id, actor]));
    const groups = markerGroups === null
      ? located.map((actor) => [actor.id])
      : markerGroups;

    return groups.map((group) => {
      const members = group.map((id) => actorById.get(id)).filter(Boolean);
      const actor = members[0];
      if (!actor) return null;
      const stacked = members.length > 1;
      const common = actor.id === selfActorId ? [] : sharedInterests(myInterests, actor.interests);
      const offset = markerOffsets[actor.id] ?? [0, 0];
      const displaced = offset[0] !== 0 || offset[1] !== 0;
      const anchorX = -offset[0];
      const anchorY = 17 - offset[1];
      return (
        <Marker
          key={actor.id}
          ref={(instance) => {
            if (instance) markerRefs.current.set(actor.id, instance);
            else markerRefs.current.delete(actor.id);
            scheduleCollisionLayout();
          }}
          longitude={actor.lng}
          latitude={actor.lat}
          anchor="bottom"
          offset={offset}
          /* MapLibre deliberately defaults covered globe markers to 20%
             opacity. Avatars should disappear behind the earth, not ghost
             through it. */
          opacityWhenCovered={0}
          onClick={(ev) => {
            ev.originalEvent.stopPropagation();
            if (stacked) focusStack(members);
            else onSelect?.(actor);
          }}
        >
          {stacked ? (
            <div
              className="marker marker-stack"
              title={`${members.length} people nearby — click to zoom in`}
              aria-label={`${members.length} people nearby; zoom in`}
            >
              <span className="marker-stack-faces">
                {members.slice(0, 3).map((member) => (
                  <Avatar key={member.id} person={member} />
                ))}
              </span>
              <span className="marker-stack-count">{members.length}</span>
              <span className="marker-name">{members.length} people</span>
            </div>
          ) : (
            <div
              className={[
                'marker',
                actor.id === selfActorId && 'self',
                // Something current, not "has ever posted": the server lets
                // the ring lapse so it keeps meaning something.
                actor.carrying && 'carrying',
                actor.replay && 'replay',
                actor.away && 'away',
                actor.live && 'live-now',
                displaced && 'displaced',
                common.length > 0 && 'common',
                unread?.[actor.id] && 'has-unread',
                selected?.id === actor.id && 'active',
              ].filter(Boolean).join(' ')}
              style={displaced ? {
                '--leader-length': `${Math.hypot(anchorX, anchorY)}px`,
                '--leader-angle': `${Math.atan2(anchorY, anchorX) * 180 / Math.PI}deg`,
              } : undefined}
              title={common.length ? `${actor.displayName} · ${common.join(', ')}` : actor.displayName}
            >
              <Avatar person={actor} online={!actor.away} />
              {common.length > 0 && (
                <span className="marker-common" title={`${common.length} shared interests`}>
                  {common.length}
                </span>
              )}
              <span className="marker-name">{actor.displayName}</span>
            </div>
          )}
        </Marker>
      );
    });
  }, [
    actors, markerGroups, markerOffsets, myInterests, selfActorId, unread, selected,
    focusStack, onSelect, scheduleCollisionLayout,
  ]);

/**
 * Clear the avatar on whichever side the popup opens, carrying the marker's
 * own collision offset through.
 */
const popupOffset = ([dx, dy]) => ({
  top: [dx, dy + 20],
  'top-left': [dx, dy + 20],
  'top-right': [dx, dy + 20],
  bottom: [dx, dy - 54],
  'bottom-left': [dx, dy - 54],
  'bottom-right': [dx, dy - 54],
  left: [dx + 20, dy - 18],
  right: [dx - 20, dy - 18],
});

  const selectedOffset = selected ? markerOffsets[selected.id] ?? [0, 0] : [0, 0];

  return (
    <div className="map-host">
      <Map
        ref={mapRef}
        mapStyle={STYLE}
        initialViewState={{ longitude: 8, latitude: 25, zoom: 2.4 }}
        minZoom={1.2}
        maxZoom={18}
        maxPitch={70}
        cursor="grab"
        onLoad={() => { setReady(true); scheduleCollisionLayout(); publishBounds(); }}
        onMove={scheduleCollisionLayout}
        onMoveEnd={() => { scheduleCollisionLayout(); publishBounds(); }}
        onResize={scheduleCollisionLayout}
        onIdle={() => { setReady(true); scheduleCollisionLayout(); }}
        onError={(ev) => setMapError(ev?.error?.message || 'the map failed to load')}
      >
        <NavigationControl position="bottom-right" visualizePitch />
        {markers}

        {selected && (
          <Popup
            longitude={selected.lng}
            latitude={selected.lat}
            /* No fixed anchor: pinned to "bottom" the card always opened
               upwards, so a marker near the top of the window pushed it off
               the screen. Left to choose, MapLibre picks the side with room —
               which needs the offset given per anchor rather than once. */
            offset={popupOffset(selectedOffset)}
            closeButton={false}
            closeOnClick={false}
            maxWidth="280px"
            className="actor-popup"
            onClose={onCloseSelected}
          >
            {renderSelected?.(selected)}
          </Popup>
        )}
      </Map>

      <div className={`loading ${ready ? 'done' : ''}`}>
        {mapError ? <span className="map-error">Map error — {mapError}</span> : 'loading the map…'}
      </div>
      <div className="map-credit">
        © OpenStreetMap · OpenFreeMap
        {footnote ? <> · {footnote}</> : null}
      </div>
    </div>
  );
});

export default MapView;
